import { FormikInput, FormikInputGroup, FormikSelect, NorwegianMobilePhoneNumberInput } from '../../../ui';
import {
    accommodationStatusCodesNo,
    AccomodationStatusValue,
    employmentStatusCodeNo,
    MartialStatusValues,
    martialStatusValues,
    trueFalseValues,
    type EmploymentStatusCode,
} from '../../../uiFieldValues/applicationFormFields';
import { amount, amountNotEmpty, amountNotNull } from '../../../validation/common/amount';
import { TFunction } from 'i18next';
import { AmountInput, EmailInput, NationalIdInput, PhoneNumberInput } from '../../../ui/CustomInput';

import { nameof } from '../../../lib/nameof';

import { stringRequired } from '../../../validation/common/string';
import { emailRequired } from '../../../validation/common/email';
import { norwegianNationalId } from '../../../validation/no/nationalId';
import { norwegianPhoneNumber } from '../../../validation/no/phoneNumber';
import { LoanNoApplicationInputModel } from '../../../serverGeneratedTypings/loanNoApplicationInputModel';
import { addPleaseSelectOption } from '../../MultiStageOnePageWizard';
import * as Yup from 'yup';
import { LoanNoApplicantData } from '../../../serverGeneratedTypings/loanNoApplicantData';
import style from '../style.module.scss';
import React from 'react';
import { applicantHasSharedEconomy, specifyAccommodationTypeOther, specifyAccomodationCost } from './helpers';

import type { WizardStage } from '@n2-common/components/MultiStageOnePageWizard/types';

export type NorwegianApplicant = Override<
    LoanNoApplicantData,
    {
        AccommodationType: AccomodationStatusValue | '';
        EmploymentStatus: EmploymentStatusCode | '';
        MaritalStatus: MartialStatusValues | '';
    }
>;
export type NorwegianLoanApplication = Override<LoanNoApplicationInputModel, { Applicant: NorwegianApplicant }>;

type ApplicantDataKey = keyof NorwegianApplicant;

export const initialApplicantValue: NorwegianApplicant = {
    Email: '',
    Phonenumber: '',
    NationalId: '',
    MaritalStatus: '',
    EmploymentStatus: '',
    AccommodationType: '',
    AccommodationCost: '',
    AccommodationTypeOther: '',
    HasStudentLoan: '',
    CarboatLoan: '',
    HasCar: '',
    YearlyGrossIncome: '',
    StudentLoan: '',
    ExtraEarningMonthly: '',
    Mortgage: '',
    PartnerIncome: '',
};

export const initialValues: NorwegianLoanApplication = {
    DesiredCredit: '',
    Applicant: initialApplicantValue,
    GAClientRef: '',
    Tracking: null,
};

export const amountBlock = (t: TFunction): WizardStage<NorwegianLoanApplication> => ({
    legend: '',
    sectionNameForTracking: 'Amount',
    fields: [
        {
            name: nameof<NorwegianLoanApplication>('DesiredCredit'),
            validation: amount(10000, 500000, t, '10 000', '500 000'),
            componentProps: {
                label: 'Hvor mye vil du låne? (10 000 - 500 000)',
            },
            Component: AmountInput,
        },
    ],
});

export const generateFieldNameForApplicant = (fieldName: ApplicantDataKey) => {
    return (nameof<NorwegianLoanApplication>('Applicant') + '.' + fieldName) as PathsOfType<NorwegianLoanApplication>;
};

export const firstBlock = (t: TFunction): WizardStage<NorwegianLoanApplication> => ({
    legend: 'Personopplysninger ',
    sectionNameForTracking: `contact information`,
    fields: [
        {
            name: generateFieldNameForApplicant('Email'),
            validation: emailRequired(t),
            componentProps: {
                label: 'E-postadresse',
            },
            Component: EmailInput,
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('Phonenumber')),
            validation: norwegianPhoneNumber(t),
            componentProps: {
                label: 'Mobilnummer',
            },
            Component: NorwegianMobilePhoneNumberInput,
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('NationalId')),
            validation: norwegianNationalId(),
            componentProps: {
                label: 'Personnummer',
            },
            Component: NationalIdInput,
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('MaritalStatus')),
            validation: stringRequired(t),
            componentProps: {
                label: 'Sivilstatus',
            },
            Component: (props) =>
                React.createElement(FormikSelect, {
                    ...props,
                    options: addPleaseSelectOption(martialStatusValues(t), t),
                }),
        },
    ],
});

export const secondBlock = (t: TFunction): WizardStage<NorwegianLoanApplication> => ({
    legend: `Økonomisk situasjon`,
    sectionNameForTracking: `Economical information`,
    fields: [
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('EmploymentStatus')),
            validation: stringRequired(t),
            componentProps: {
                label: 'Arbeidssituasjon',
            },
            Component: (props) =>
                React.createElement(FormikSelect, {
                    //
                    options: addPleaseSelectOption(employmentStatusCodeNo, t),
                    ...props,
                }),
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('YearlyGrossIncome')),
            validation: amountNotNull(t),
            componentProps: {
                label: 'Brutto årsinntekt (før skatt)',
            },
            Component: AmountInput,
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('AccommodationType')),
            validation: stringRequired(t),
            componentProps: {
                label: 'Boforhold',
            },
            Component: (props) => React.createElement(FormikSelect, { ...props, options: addPleaseSelectOption(accommodationStatusCodesNo, t) }),
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('AccommodationTypeOther')),
            validation: Yup.string().when('AccommodationType', {
                is: (val: NorwegianApplicant['AccommodationType']) => specifyAccommodationTypeOther(val),
                then: () => stringRequired(t),
                otherwise: () => Yup.string().nullable(),
            }),
            componentProps: {
                label: 'Spesifiser ditt boforhold',
            },
            hidden: (values) => !specifyAccommodationTypeOther(values['Applicant'].AccommodationType),
            Component: (props) => React.createElement(FormikInput, { ...props, type: 'text' }),
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('AccommodationCost')),
            validation: Yup.number().when('AccommodationType', {
                is: (val: NorwegianApplicant['AccommodationType']) => specifyAccomodationCost(val),
                then: () => amountNotEmpty('leiekostnader', t),
                otherwise: () => Yup.number().nullable(),
            }),
            componentProps: (values) => ({
                label: applicantHasSharedEconomy(values['Applicant'].MaritalStatus)
                    ? 'Husholdningens leiekostnader/fellesutgifter'
                    : 'Leiekostnader/fellesutgifter',
            }),
            hidden: (values) => !specifyAccomodationCost(values['Applicant'].AccommodationType),
            Component: AmountInput,
        },
    ],
});

export const thirdBlock = (t: TFunction): WizardStage<NorwegianLoanApplication> => ({
    legend: (values) => (applicantHasSharedEconomy(values['Applicant'].MaritalStatus) ? 'Gjeld - husholdningens totale' : 'Gjeld'),
    sectionNameForTracking: `Debt information`,
    fields: [
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('HasCar')),
            validation: stringRequired(t),
            componentProps: (values) => ({
                label: applicantHasSharedEconomy(values['Applicant'].MaritalStatus)
                    ? 'Eier eller leaser husholdningen bil?'
                    : 'Eier eller leaser du bil?',
            }),
            Component: (props) =>
                React.createElement(FormikInputGroup, {
                    ...props,
                    type: 'radio',
                    options: trueFalseValues(t),
                    className: style.radioButtons,
                    horisontal: true,
                }),
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('ExtraEarningMonthly')),
            validation: amount(0, Number.MAX_SAFE_INTEGER, t),
            componentProps: (values) => ({
                label: applicantHasSharedEconomy(values['Applicant'].MaritalStatus)
                    ? 'Månedlig skattefri leieinntekt i husholdningen'
                    : 'Månedlig skattefri leieinntekt',
                helptext: applicantHasSharedEconomy(values['Applicant'].MaritalStatus)
                    ? 'Oppgi husholdningens skattefrie inntekt ved utleie av bolig. Skriv 0 om dere ikke har noen leieinntekter.'
                    : 'Oppgi din skattefrie inntekt ved utleie av bolig. Skriv 0 om du ikke har noen leieinntekter.',
            }),
            Component: AmountInput,
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('PartnerIncome')),
            validation: Yup.number().when('MaritalStatus', {
                is: (val: NorwegianApplicant['MaritalStatus']) => applicantHasSharedEconomy(val),
                then: () => amountNotEmpty('Partners brutto årsinntekt', t),
                otherwise: () => Yup.number().nullable(),
            }),
            componentProps: {
                label: 'Partners brutto årsinntekt',
            },
            hidden: (values) => !applicantHasSharedEconomy(values['Applicant'].MaritalStatus),
            Component: AmountInput,
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('Mortgage')),
            validation: amountNotNull(t),
            componentProps: (values) => ({
                label: applicantHasSharedEconomy(values['Applicant'].MaritalStatus) ? 'Husholdningens samlede boliggjeld' : 'Boliggjeld (din andel)',
            }),
            Component: AmountInput,
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('StudentLoan')),
            validation: amountNotNull(t),
            componentProps: {
                label: 'Studiegjeld',
                helptext: 'Din nåværende personlige studiegjeld.',
            },
            Component: AmountInput,
        },
        {
            name: generateFieldNameForApplicant(nameof<NorwegianApplicant>('CarboatLoan')),
            validation: amountNotNull(t),
            componentProps: {
                label: 'Gjeld til bil / båt / MC / bobil',
                helptext: 'Din nåværende personlige andel gjeld til bil/båt/MC/bobil (cirkabeløp).',
            },
            Component: AmountInput,
        },
    ],
});
